import { FunctionComponent, useState, useMemo } from 'react';
import type { ButtonFragment } from '../__generated__/button';
import classNames from 'classnames';

/**
 *   Cant handle light-mode because of hardcoded outline colors.
 * */

const HEX_COLOR_REGEX = /^#?([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

export const ButtonFragmentUI: FunctionComponent<{
  btn: ButtonFragment;
  onClick: () => void;
}> = ({ btn, onClick }) => {
  const { isActive, isFocused, handlers } = useButtonInteraction();

  const backgroundColor = isActive
    ? hexToRgba(btn.focusedBackgroundColor, btn.focusedBackgroundOpacityInPct)
    : hexToRgba(btn.backgroundColor, btn.backgroundOpacityInPct);

  const textColor = isActive ? (btn.focusedTextColor ?? undefined) : (btn.textColor ?? undefined);

  return (
    <button
      onClick={onClick}
      {...handlers}
      className={classNames(
        'inline-flex flex-none',
        'whitespace-nowrap gap-2 rounded-full items-center border-none',
        'text-button-label-m h-10 768:text-button-label-l 768:h-12',
        'pl-6 768:pl-8',
        'pr-6 768:pr-8'
      )}
      style={{
        backgroundColor,
        color: textColor,
        outline:
          isFocused && btn.outlineColor ? `2px solid ${btn.outlineColor}` : '2px solid transparent',
        outlineOffset: '2px',
      }}
      type="button"
    >
      {btn.text}
    </button>
  );
};

const hexToRgba = (hex: string | null, opacityPct: number | null): string | undefined => {
  if (!hex || !HEX_COLOR_REGEX.test(hex)) return undefined;

  const opacity = opacityPct != null ? Math.max(0, Math.min(100, opacityPct)) / 100 : 1;
  let cleanHex = hex.replace('#', '');

  // Expand shorthand hex (e.g., "fff" -> "ffffff")
  if (cleanHex.length === 3) {
    cleanHex = cleanHex
      .split('')
      .map(char => char + char)
      .join('');
  }

  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

function useButtonInteraction() {
  const [isHovered, setIsHovered] = useState(false);
  const [isPressed, setIsPressed] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const isActive = isHovered || isPressed || isFocused;

  const handlers = useMemo(
    () => ({
      onMouseEnter: () => setIsHovered(true),
      onMouseLeave: () => {
        setIsHovered(false);
        setIsPressed(false);
      },
      onMouseDown: () => setIsPressed(true),
      onMouseUp: () => setIsPressed(false),
      onFocus: () => setIsFocused(true),
      onBlur: () => setIsFocused(false),
      onTouchStart: () => setIsPressed(true),
      onTouchEnd: () => setIsPressed(false),
    }),
    []
  );

  return { isActive, isFocused, handlers };
}
